import { Form, FormRenderProps } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { Stack, Button } from '@mui/material';

import { Page, useAppLayoutContextConsumer, LinkItem } from '@occc-app/design-system';

import { AdSetFormValues } from '../../touchpoint.types';
import { validate, hasRequiredFieldErrors } from '../../utils/ad-sets-form.validation';
import { AdSetForm } from '../../components/ad-set-form';
import { CANCEL_BUTTON_TEXT, SAVE_BUTTON_TEXT } from '../../touchpoint.constants';

const PAGE_TITLE = 'Add Ad Set';
const PAGE_SUBTITLE =
  'Create and manage ad sets for your in-store campaigns. Set scheduling and define targeting to control when and where ads are shown.';

const EMPTY_SCHEDULE = { days: [], startTime: '', endTime: '' };

const initialValues: Partial<AdSetFormValues> = {
  name: '',
  description: '',
  assetId: null,
  offer: null,
  kioskIds: [],
  schedules: [EMPTY_SCHEDULE],
};

type AdSetsCreatePageProps = {
  breadcrumbLinks: LinkItem[];
  onSubmit: (values: AdSetFormValues) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
};

export const AdSetsCreatePage = ({
  breadcrumbLinks,
  onSubmit,
  onCancel,
  isSubmitting,
}: AdSetsCreatePageProps) => {
  useAppLayoutContextConsumer(breadcrumbLinks);

  const renderForm = ({ handleSubmit, errors, submitting }: FormRenderProps<AdSetFormValues>) => {
    const isDisabled = submitting || isSubmitting || hasRequiredFieldErrors(errors);

    return (
      <Page
        browserTabTitle={PAGE_TITLE}
        pageHeaderProps={{
          title: PAGE_TITLE,
          subtitle: PAGE_SUBTITLE,
        }}
        actions={
          <Stack direction='row' spacing={1}>
            <Button variant='outlined' color='secondary' onClick={onCancel}>
              {CANCEL_BUTTON_TEXT}
            </Button>
            <Button
              variant='contained'
              color='secondary'
              onClick={handleSubmit}
              disabled={isDisabled}
            >
              {SAVE_BUTTON_TEXT}
            </Button>
          </Stack>
        }
      >
        <AdSetForm handleSubmit={handleSubmit} />
      </Page>
    );
  };

  return (
    <Form<AdSetFormValues>
      onSubmit={onSubmit}
      render={renderForm}
      validate={validate}
      initialValues={initialValues}
      mutators={{ ...arrayMutators }}
    />
  );
};
